"use client";

import { useState, useRef, useEffect } from "react";
import type { Locale } from "@/lib/i18n/config";
import { getDictionary } from "@/lib/i18n/dictionaries";

export function ContactForm({ locale }: { locale: Locale }) {
  const dict = getDictionary(locale);
  const t = dict.contactForm;
  const common = dict.common;

  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tidspunkt for hvornår formularen blev indlæst i browseren (timing-trap mod bots).
  const loadedAtRef = useRef<number>(0);
  useEffect(() => {
    loadedAtRef.current = Date.now();
  }, []);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);

    const data = new FormData(e.currentTarget);
    const payload = {
      name: data.get("name"),
      email: data.get("email"),
      phone: data.get("phone") || undefined,
      message: data.get("message"),
      source: "contact",
      // Anti-spam — læses og forkastes server-side, gemmes aldrig:
      homepage: data.get("homepage") || "", // honeypot: skal være tom
      elapsedMs: Date.now() - loadedAtRef.current, // timing-trap (ms siden load)
    };

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.error || t.errorDefault);
      }
      setDone(true);
      (e.target as HTMLFormElement).reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.errorDefault);
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <div className="card text-center py-10 max-w-[520px] mx-auto">
        <div className="text-[42px] mb-3">✓</div>
        <h3 className="font-[family-name:var(--font-display-loaded)] text-[22px] font-bold text-[var(--color-navy-700)] mb-2">
          {t.successTitle}
        </h3>
        <p className="text-[var(--color-muted)] text-[15px]">{t.successBody}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card max-w-[520px] mx-auto !p-7">
      {/* Honeypot mod spam-bots: skjult off-screen (bevidst IKKE inline display:none,
          som visse bots ignorerer). Mennesker ser det aldrig; bots udfylder ofte alle
          felter — er det udfyldt, forkastes indsendelsen lydløst på serveren. */}
      <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
        <label htmlFor="cf-homepage">Lad dette felt stå tomt</label>
        <input id="cf-homepage" name="homepage" type="text" tabIndex={-1} autoComplete="off" />
      </div>
      <div className="mb-4">
        <label htmlFor="cf-name" className="label">{common.name}</label>
        <input id="cf-name" name="name" type="text" required minLength={2} className="input" />
      </div>
      <div className="mb-4">
        <label htmlFor="cf-email" className="label">{common.email}</label>
        <input id="cf-email" name="email" type="email" required className="input" />
      </div>
      <div className="mb-4">
        <label htmlFor="cf-phone" className="label">{common.phoneOptional}</label>
        <input id="cf-phone" name="phone" type="tel" className="input" />
      </div>
      <div className="mb-5">
        <label htmlFor="cf-msg" className="label">{common.message}</label>
        <textarea id="cf-msg" name="message" rows={4} required minLength={2} className="textarea" />
      </div>
      {error && (
        <div className="mb-4 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
          {error}
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="btn btn-primary w-full justify-center disabled:opacity-60"
      >
        {submitting ? common.sending : common.sendMessage}
      </button>
    </form>
  );
}
